/**
 * The group's other companies: what we say, what they say, and the gap.
 *
 * This system keeps one company's books. When money moves between us and
 * another company in the group, our side is posted and **their side is
 * mirrored** — written down as the entry their books should carry, never
 * posted here. The confirmation is what turns that from an assumption into a
 * fact, and a difference is shown per component, because "we differ by ₹3,750
 * of interest" is a question somebody can answer and "we differ" is not.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'

interface Summary {
  as_at: string
  companies: {
    party_id: string
    party_name: string
    principal_paise: number
    interest_paise: number
    investments_paise: number
    instruments: number
    last_confirmed_as_at: string | null
    confirmation_status: string | null
    difference_principal_paise: number
  }[]
  differences: {
    confirmation_id: string
    party_id: string
    party_name: string
    as_at: string
    principal_paise: number
    interest_paise: number
    tds_paise: number
  }[]
  unconfirmed: string[]
}

interface Statement {
  party_id: string
  party_name: string
  as_at: string
  our_principal_paise: number
  our_interest_paise: number
  our_tds_paise: number
  instruments: {
    reference: string
    title: string
    family: string
    principal_paise: number
    interest_paise: number
    status: string
  }[]
  mirror_lines: {
    event_date: string
    component: string
    amount_paise: number
    their_line: { Dr?: string; Cr?: string; amount_paise?: number; note?: string }
    narration: string | null
  }[]
  investments: {
    investee_name: string
    share_class_label: string
    units: number
    cost_paise: number
  }[]
}

interface Confirmation {
  id: string
  party_id: string
  party_name: string
  as_at: string
  our_principal_paise: number
  our_interest_paise: number
  our_tds_paise: number
  their_principal_paise: number | null
  their_interest_paise: number | null
  their_tds_paise: number | null
  difference_principal_paise: number
  difference_interest_paise: number
  difference_tds_paise: number
  status: string
  resolution: string | null
}

const today = () => new Date().toISOString().slice(0, 10)

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

export default function GroupCompanies() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['group'] })

  const summary = useQuery({
    queryKey: ['group', 'summary'],
    queryFn: () => api.get<Summary>('/financing/intercompany'),
  })
  const confirmations = useQuery({
    queryKey: ['group', 'confirmations'],
    queryFn: () => api.get<Confirmation[]>('/financing/intercompany/confirmations'),
  })
  const statement = useQuery({
    queryKey: ['group', 'statement', open],
    queryFn: () => api.get<Statement>(`/financing/intercompany/${open}/statement`),
    enabled: open !== null,
  })

  const resolve = useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      api.post(`/financing/intercompany/confirmations/${id}/resolve`, { resolution }),
    onSuccess: refresh,
  })

  if (summary.isLoading) return <Loading what="Loading the group position" />
  const figures = summary.data
  const owed = (figures?.companies ?? []).reduce((sum, row) => sum + row.principal_paise, 0)

  return (
    <>
      <div className="grid">
        <Stat
          label="Group companies"
          value={figures?.companies.length ?? 0}
          note="marked as one on their financing profile"
        />
        <Stat
          label="Net balance"
          value={formatInr(owed)}
          note={owed >= 0 ? 'owed to us' : 'owed by us'}
        />
        <Stat
          label="Unconfirmed"
          value={figures?.unconfirmed.length ?? 0}
          note="blocking at the year end"
        />
        <Stat
          label="Differences open"
          value={figures?.differences.length ?? 0}
          note="their books against ours"
        />
      </div>

      <Card title="Where each company stands" hint={`as at ${figures?.as_at ?? ''}`} tight>
        <ErrorNote error={summary.error} />
        <Note>
          Our side is posted here; their side is written down as the entry their books should
          carry. A balance is only true when both companies agree it, which is what the
          confirmations below are for.
        </Note>
        {(figures?.companies.length ?? 0) === 0 ? (
          <div className="empty">
            No company is marked as part of the group yet. Mark one on its financing profile, under
            the vendor.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th className="num">Principal</th>
                <th className="num">Interest</th>
                <th className="num">Investments</th>
                <th className="num">Loans</th>
                <th>Last confirmed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(figures?.companies ?? []).map((row) => (
                <tr key={row.party_id}>
                  <td>{row.party_name}</td>
                  <td className="num">
                    {formatInr(row.principal_paise)}
                    <div className="faint">
                      {row.principal_paise >= 0 ? 'owed to us' : 'owed by us'}
                    </div>
                  </td>
                  <td className="num">{formatInr(row.interest_paise)}</td>
                  <td className="num">{formatInr(row.investments_paise)}</td>
                  <td className="num">{row.instruments}</td>
                  <td>
                    {row.last_confirmed_as_at ? (
                      <>
                        <div className="date">{row.last_confirmed_as_at}</div>
                        <Pill value={row.confirmation_status ?? 'sent'} />
                      </>
                    ) : (
                      <span className="faint">never</span>
                    )}
                  </td>
                  <td className="num">
                    <button
                      className="small"
                      onClick={() => setOpen(open === row.party_id ? null : row.party_id)}
                    >
                      Statement
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && statement.data && (
        <StatementCard
          statement={statement.data}
          onRecorded={() => {
            refresh()
            void queryClient.invalidateQueries({ queryKey: ['group', 'statement'] })
          }}
        />
      )}

      <Card title="Confirmations" hint={`${(confirmations.data ?? []).length}`} tight>
        <ErrorNote error={confirmations.error ?? resolve.error} />
        {(confirmations.data ?? []).length === 0 ? (
          <div className="empty">No confirmations have been exchanged yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>As at</th>
                <th className="num">Ours</th>
                <th className="num">Theirs</th>
                <th className="num">Difference</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(confirmations.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td>{row.party_name}</td>
                  <td className="date">{row.as_at}</td>
                  <td className="num">{formatInr(row.our_principal_paise)}</td>
                  <td className="num">
                    {row.their_principal_paise === null ? (
                      <span className="faint">not received</span>
                    ) : (
                      formatInr(row.their_principal_paise)
                    )}
                  </td>
                  <td className="num">
                    {formatInr(row.difference_principal_paise)}
                    {row.difference_interest_paise !== 0 && (
                      <div className="faint">
                        interest {formatInr(row.difference_interest_paise)}
                      </div>
                    )}
                    {row.difference_tds_paise !== 0 && (
                      <div className="faint">tax {formatInr(row.difference_tds_paise)}</div>
                    )}
                  </td>
                  <td>
                    <Pill value={row.status} />
                    {row.resolution && <div className="faint">{row.resolution}</div>}
                  </td>
                  <td className="num">
                    {row.status === 'received_difference' && can('fin.intercompany.approve') && (
                      <ResolveButton
                        onResolve={(resolution) => resolve.mutate({ id: row.id, resolution })}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}

function ResolveButton({ onResolve }: { onResolve: (resolution: string) => void }) {
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  if (!open)
    return (
      <button className="small" onClick={() => setOpen(true)}>
        Explain it
      </button>
    )
  return (
    <>
      <input
        value={text}
        placeholder="what the difference was"
        onChange={(event) => setText(event.target.value)}
      />{' '}
      <button
        className="small primary"
        disabled={text.trim().length < 3}
        onClick={() => onResolve(text)}
      >
        Save
      </button>
    </>
  )
}

function StatementCard({
  statement,
  onRecorded,
}: {
  statement: Statement
  onRecorded: () => void
}) {
  const { can } = useAuth()
  const [asAt, setAsAt] = useState(today())
  const [principal, setPrincipal] = useState('')
  const [interest, setInterest] = useState('')
  const [tds, setTds] = useState('')

  const record = useMutation({
    mutationFn: () =>
      api.post('/financing/intercompany/confirmations', {
        party_id: statement.party_id,
        as_at: asAt,
        their_principal_paise: toPaise(principal),
        their_interest_paise: toPaise(interest),
        their_tds_paise: toPaise(tds),
      }),
    onSuccess: onRecorded,
  })

  return (
    <Card title={`${statement.party_name} — as at ${statement.as_at}`}>
      <ErrorNote error={record.error} />
      <div className="grid">
        <Stat label="Principal" value={formatInr(statement.our_principal_paise)} note="our books" />
        <Stat label="Interest" value={formatInr(statement.our_interest_paise)} note="our books" />
        <Stat label="Tax withheld" value={formatInr(statement.our_tds_paise)} note="to date" />
      </div>

      <h4>Loans with this company</h4>
      {statement.instruments.length === 0 ? (
        <div className="empty">No loans either way.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Reference</th>
              <th>What</th>
              <th>Direction</th>
              <th className="num">Principal</th>
              <th className="num">Interest</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {statement.instruments.map((row) => (
              <tr key={row.reference}>
                <td className="mono">{row.reference}</td>
                <td>{row.title}</td>
                <td className="muted">{row.family === 'lending' ? 'we lent' : 'we borrowed'}</td>
                <td className="num">{formatInr(row.principal_paise)}</td>
                <td className="num">{formatInr(row.interest_paise)}</td>
                <td>
                  <Pill value={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {statement.investments.length > 0 && (
        <>
          <h4>Shares we hold in them</h4>
          <table>
            <thead>
              <tr>
                <th>Class</th>
                <th className="num">Shares</th>
                <th className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {statement.investments.map((row) => (
                <tr key={row.share_class_label}>
                  <td className="muted">{row.share_class_label}</td>
                  <td className="num">{row.units.toLocaleString('en-IN')}</td>
                  <td className="num">{formatInr(row.cost_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h4>What their books should carry</h4>
      <Note>
        These lines are written when ours post. They are not entries in this system — the other
        company keeps its own books — but they are what a confirmation is checked against.
      </Note>
      {statement.mirror_lines.length === 0 ? (
        <div className="empty">Nothing has moved with this company yet.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Component</th>
              <th className="num">Amount</th>
              <th>Their entry</th>
            </tr>
          </thead>
          <tbody>
            {statement.mirror_lines.map((row, index) => (
              <tr key={`${row.event_date}-${row.component}-${index}`}>
                <td className="date">{row.event_date}</td>
                <td className="muted">{row.component}</td>
                <td className="num">{formatInr(row.amount_paise)}</td>
                <td className="muted">
                  Dr {row.their_line.Dr} · Cr {row.their_line.Cr}
                  {row.their_line.note && <div className="faint">{row.their_line.note}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {can('fin.intercompany.create') && (
        <>
          <h4>Record what they confirm</h4>
          <div className="row">
            <Field label="As at">
              <input type="date" value={asAt} onChange={(event) => setAsAt(event.target.value)} />
            </Field>
            <Field label="Their principal">
              <input value={principal} onChange={(event) => setPrincipal(event.target.value)} />
            </Field>
            <Field label="Their interest">
              <input value={interest} onChange={(event) => setInterest(event.target.value)} />
            </Field>
            <Field label="Their tax withheld">
              <input value={tds} onChange={(event) => setTds(event.target.value)} />
            </Field>
          </div>
          <button
            className="primary"
            disabled={record.isPending || !principal}
            onClick={() => record.mutate()}
          >
            Save their figures
          </button>
        </>
      )}
    </Card>
  )
}
